import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, EmptyState } from "@/components/app-shell";
import { Package, Plus, Search, Pencil, Archive, ArchiveRestore, X, EyeOff, Star, Loader2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { computeSalePrice, resolveMarkup } from "@/lib/calc";
import { useActiveCompany } from "@/hooks/use-active-company";
import { BulkPricingDialog } from "@/components/materials/BulkPricingDialog";
import { useMaterialsSearch, useDebounced, isMissingDiscount } from "@/hooks/use-materials-search";

const LIST_DISCOUNT_KEY = "verqo.materials.listDiscountPercent";




type Material = {
  id: string;
  item_number: string | null;
  description: string;
  category_id: string | null;
  unit: string;
  purchase_price: number;
  list_price: number | null;
  markup_percent: number | null;
  supplier: string | null;
  supplier_id: string | null;
  manufacturer: string | null;
  is_small_part: boolean;
  is_favorite: boolean;
  is_active: boolean;
  image_url: string | null;
  customer_price: number | null;
  customer_price_source: "manual" | "markup" | "list_discount" | null;
  customer_price_set_at: string | null;
};

/** Dekningsbidrag og dekningsgrad for én vare (eks. mva). */
function margin(customerPrice: number | null | undefined, cost: number) {
  const price = Number(customerPrice ?? 0);
  if (!price) return null;
  const db = price - Number(cost ?? 0);
  return { db, dg: (db / price) * 100 };
}

function marginTone(dg: number) {
  if (dg < 0) return "text-destructive";
  if (dg < 15) return "text-warning";
  return "text-success";
}


type Supplier = { id: string; name: string };
type Category = { id: string; name: string; sort_order: number };

export const Route = createFileRoute("/_authenticated/materials")({
  head: () => ({
    meta: [
      { title: "Materiell | VERQO" },
      { name: "description", content: "Materialregister for VERQO." },
    ],
  }),
  component: MaterialsPage,
});

function MaterialsPage() {
  const qc = useQueryClient();
  const { activeModuleKey: moduleKey, activeModule: moduleDef, companyId } = useActiveCompany();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);
  const [onlyMissingDiscount, setOnlyMissingDiscount] = useState(false);
  const [onlyPriceCheck, setOnlyPriceCheck] = useState(false);
  const [editing, setEditing] = useState<Partial<Material> | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Nullstill kategorifilter ved bytte av fag – kategorier er fag-spesifikke.
  useEffect(() => { setCategoryId(""); }, [moduleKey]);

  const debouncedSearch = useDebounced(search, 250);

  // Alt søk og all paginering skjer på serveren. Vi henter aldri hele
  // registeret til klienten – 45 000+ varer tok over to minutter.
  useEffect(() => { setPage(0); }, [debouncedSearch, categoryId, showArchived, onlyMissingDiscount, onlyPriceCheck, moduleKey]);

  const {
    rows: materials,
    total,
    totalLoading,
    isLoading,
    isFetching,
  } = useMaterialsSearch({
    moduleKey,
    companyId,
    search: debouncedSearch,
    categoryId: categoryId || null,
    archived: showArchived,
    missingDiscount: onlyMissingDiscount,
    needsPriceCheck: onlyPriceCheck,
    page,
    pageSize: PAGE_SIZE,
  });

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));




  const { data: markupCtx } = useQuery({
    queryKey: ["category-markups"],
    queryFn: async () => {
      const [s, cm] = await Promise.all([
        supabase.from("settings").select("default_markup_percent").maybeSingle(),
        supabase.from("material_category_markups").select("category_id,markup_percent"),
      ]);
      const catMap: Record<string, number> = {};
      for (const r of cm.data ?? []) if (r.category_id) catMap[r.category_id] = Number(r.markup_percent);
      return {
        defaultMarkup: Number(s.data?.default_markup_percent ?? 35),
        categoryMarkups: catMap,
      };
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["material-categories", moduleKey],
    queryFn: async () => {
      const { data, error } = await supabase.from("material_categories").select("id,name,sort_order,is_active").eq("is_active", true).eq("module_key", moduleKey).order("sort_order");
      if (error) throw error;
      return data as (Category & { is_active: boolean })[];
    },
  });


  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("suppliers").select("id,name,is_active").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data as (Supplier & { is_active: boolean })[];
    },
  });

  const categoryName = (id: string | null) => categories.find((c) => c.id === id)?.name ?? null;


  const archive = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase.from("materials").update({ is_active: val }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["materials"] }); toast.success(v.val ? "Aktivert" : "Arkivert"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleFav = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase.from("materials").update({ is_favorite: val }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["materials"] }),
  });

  return (
    <>
      <PageHeader
        title={`Materiell — ${moduleDef.label}`}
        subtitle={`${totalLoading ? "…" : total.toLocaleString("nb-NO")} ${showArchived ? "utgåtte" : "aktive"} varer i faget`}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/settings/import/$entity" params={{ entity: "materials" }} className="btn btn-secondary">
              <Upload className="h-4 w-4" /> Last opp prisfil
            </Link>
            <button onClick={() => setBulkOpen(true)} className="btn btn-secondary">
              Masseendre pris
            </button>
            <button onClick={() => setEditing({ unit: "stk", category_id: categories[0]?.id ?? null })} className="btn btn-primary">
              <Plus className="h-4 w-4" /> Ny vare
            </button>
          </div>
        }
      />


      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="input-base pl-9" placeholder="Søk vare, varenr, leverandør, produsent…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input-base sm:w-56" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">Alle kategorier</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Vis utgåtte varer
        </label>
        <label className="inline-flex items-center gap-2 text-sm" title="Varer med veiledende pris, men uten registrert innkjøpsrabatt">
          <input type="checkbox" checked={onlyMissingDiscount} onChange={(e) => setOnlyMissingDiscount(e.target.checked)} />
          Kun «rabatt mangler»
        </label>
        <label className="inline-flex items-center gap-2 text-sm" title="Varer som er flagget for manuell kontroll av pris mot leverandør">
          <input type="checkbox" checked={onlyPriceCheck} onChange={(e) => setOnlyPriceCheck(e.target.checked)} />
          Kun «kontroller pris»
        </label>
      </div>

      <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>
          {totalLoading ? "…" : total.toLocaleString("nb-NO")} {total === 1 ? "vare" : "varer"}
          {debouncedSearch ? ` for «${debouncedSearch}»` : ""}
          {total > 0 && ` · viser ${(page * PAGE_SIZE + 1).toLocaleString("nb-NO")}–${Math.min(total, (page + 1) * PAGE_SIZE).toLocaleString("nb-NO")}`}
        </span>
        {isFetching && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Oppdaterer…</span>}
      </div>

      {isLoading ? (
        <div className="card flex items-center justify-center gap-2 px-4 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Henter varer…
        </div>
      ) : materials.length === 0 ? (
        <EmptyState
          icon={Package}
          title={debouncedSearch || categoryId ? "Ingen treff" : "Ingen varer ennå"}
          description={debouncedSearch || categoryId ? "Juster søket eller kategorifilteret." : "Legg til varer for å bygge opp materialregisteret ditt."}
          action={<button onClick={() => setEditing({ unit: "stk", category_id: categories[0]?.id ?? null })} className="btn btn-primary"><Plus className="h-4 w-4" /> Ny vare</button>}
        />
      ) : (

        <div className="card divide-y divide-border">
          {materials.map((m) => {
            const effectiveMarkup = markupCtx ? resolveMarkup({
              productMarkup: m.markup_percent,
              categoryId: m.category_id,
              categoryMarkups: markupCtx.categoryMarkups,
              defaultMarkup: markupCtx.defaultMarkup,
            }) : Number(m.markup_percent ?? 0);
            const sale = computeSalePrice(Number(m.purchase_price ?? 0), effectiveMarkup);
            const catName = categoryName(m.category_id);
            const supName = suppliers.find((s) => s.id === m.supplier_id)?.name ?? m.supplier;
            return (
              <div key={m.id} className={`flex items-center gap-3 px-4 py-3 ${m.is_active ? "" : "opacity-60"}`}>
                <button
                  onClick={() => toggleFav.mutate({ id: m.id, val: !m.is_favorite })}
                  className={`btn btn-ghost p-1.5 ${m.is_favorite ? "text-warning" : "text-muted-foreground"}`}
                  title={m.is_favorite ? "Fjern favoritt" : "Marker som favoritt"}
                >
                  <Star className={`h-4 w-4 ${m.is_favorite ? "fill-current" : ""}`} />
                </button>
                {m.image_url ? (
                  <img src={m.image_url} alt="" loading="lazy" className="h-12 w-12 flex-none rounded border border-border object-contain bg-white" />
                ) : (
                  <div className="h-12 w-12 flex-none rounded border border-dashed border-border" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {m.item_number && <span className="text-xs font-mono text-muted-foreground">{m.item_number}</span>}
                    {catName && <span className="chip">{catName}</span>}
                    {m.is_small_part && <span className="chip" title="Skjules på tilbud"><EyeOff className="h-3 w-3" />Smådel</span>}
                    {!m.is_active && <span className="chip bg-muted text-muted-foreground">Utgått</span>}
                    {m.external_reference === "kontroller_pris" && (
                      <span className="chip bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-200"
                        title="Prisen er rettet etter feil i leverandørfilen – kontroller mot leverandør">
                        Kontroller pris mot leverandør
                      </span>
                    )}
                    {isMissingDiscount(m) && (
                      <span className="chip bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                        title="Innkjøpsprisen er lik veiledende pris – ingen rabatt er registrert">
                        Rabatt mangler – kontroller mot leverandør
                      </span>
                    )}
                    <span className="chip" title="Effektivt påslag">+{effectiveMarkup.toFixed(0)}%</span>
                  </div>
                  <div className="mt-0.5 truncate text-sm font-medium">{m.description}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Innkjøp {formatCurrency(m.purchase_price)} · Salg {formatCurrency(sale)} / {m.unit}
                    {m.list_price != null && ` · Ord. ${formatCurrency(m.list_price)}`}
                    {m.list_price != null && m.list_price > 0 && m.purchase_price < m.list_price &&
                      ` · Rabatt ${((1 - m.purchase_price / m.list_price) * 100).toFixed(1).replace(".", ",")}%`}
                    {m.manufacturer && ` · ${m.manufacturer}`}
                    {supName && ` · ${supName}`}
                  </div>
                  {(() => {
                    const mg = margin(m.customer_price, Number(m.purchase_price ?? 0));
                    if (!mg) {
                      return <div className="mt-0.5 text-xs text-muted-foreground">Kundepris: <span className="italic">ikke satt</span></div>;
                    }
                    return (
                      <div className="mt-0.5 text-xs">
                        <span className="font-medium">Kundepris {formatCurrency(Number(m.customer_price))}</span>
                        <span className={`ml-2 ${marginTone(mg.dg)}`}>
                          DB {formatCurrency(mg.db)} · DG {mg.dg.toFixed(1).replace(".", ",")} %
                        </span>
                      </div>
                    );
                  })()}

                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(m)} className="btn btn-ghost p-2"><Pencil className="h-4 w-4" /></button>
                  <button
                    onClick={() => archive.mutate({ id: m.id, val: !m.is_active })}
                    className="btn btn-ghost p-2"
                    title={m.is_active ? "Arkiver" : "Aktiver"}
                  >
                    {m.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pageCount > 1 && !isLoading && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <button className="btn btn-secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Forrige
          </button>
          <span className="text-muted-foreground">Side {page + 1} av {pageCount.toLocaleString("nb-NO")}</span>
          <button className="btn btn-secondary" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Neste
          </button>
        </div>
      )}


      {bulkOpen && (
        <BulkPricingDialog
          moduleKey={moduleKey}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setBulkOpen(false)}
        />
      )}

      {editing && (
        <MaterialModal
          initial={editing}
          moduleKey={moduleKey}
          markupCtx={markupCtx}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function MaterialModal({
  initial, moduleKey, markupCtx, categories, suppliers, onClose,
}: {
  initial: Partial<Material>;
  moduleKey: import("@/modules").ModuleKey;
  markupCtx: { defaultMarkup: number; categoryMarkups: Record<string, number> } | undefined;
  categories: Category[];
  suppliers: Supplier[];
  onClose: () => void;
}) {

  const qc = useQueryClient();
  const { companyId } = useActiveCompany();
  const [form, setForm] = useState<Partial<Material>>(initial);
  const isEdit = Boolean(initial.id);

  // Sist brukte rabatt fra veiledende, huskes per bruker/nettleser.
  const [listDiscountInput, setListDiscountInput] = useState("0");
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LIST_DISCOUNT_KEY);
      if (stored != null && stored !== "") setListDiscountInput(stored);
    } catch { /* ignore */ }
  }, []);
  const listDiscount = Number.isNaN(Number(listDiscountInput)) ? 0 : Number(listDiscountInput || 0);

  const set = <K extends keyof Material>(k: K, v: Material[K]) =>
    setForm((f) => ({ ...f, [k]: v }));


  const effectiveMarkup = useMemo(() => {
    if (!markupCtx) return Number(form.markup_percent ?? 35);
    return resolveMarkup({
      productMarkup: form.markup_percent,
      categoryId: form.category_id,
      categoryMarkups: markupCtx.categoryMarkups,
      defaultMarkup: markupCtx.defaultMarkup,
    });
  }, [markupCtx, form.markup_percent, form.category_id]);

  const dynamicSale = computeSalePrice(Number(form.purchase_price ?? 0), effectiveMarkup);
  const usesOverride = form.markup_percent != null && !Number.isNaN(Number(form.markup_percent));

  const cost = Number(form.purchase_price ?? 0);
  const liveMargin = margin(form.customer_price, cost);

  const setCustomerPrice = (value: number | null, source: Material["customer_price_source"]) =>
    setForm((f) => ({ ...f, customer_price: value, customer_price_source: value == null ? null : source }));

  const save = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      const customerPrice =
        form.customer_price == null || Number.isNaN(Number(form.customer_price)) ? null : Number(form.customer_price);
      const payload = {
        item_number: form.item_number || null,
        description: (form.description || "").trim(),
        category_id: form.category_id || null,
        unit: form.unit || "stk",
        purchase_price: Number(form.purchase_price ?? 0),
        list_price: form.list_price == null || Number.isNaN(Number(form.list_price)) ? null : Number(form.list_price),
        markup_percent: usesOverride ? Number(form.markup_percent) : null,
        supplier_id: form.supplier_id || null,
        supplier: form.supplier || null,
        manufacturer: form.manufacturer || null,
        is_small_part: Boolean(form.is_small_part),
        is_favorite: Boolean(form.is_favorite),
        customer_price: customerPrice,
        customer_price_source: customerPrice == null ? null : (form.customer_price_source ?? "manual"),
      };

      if (!payload.description) throw new Error("Beskrivelse er påkrevd");
      if (isEdit && initial.id) {
        const { error } = await supabase.from("materials").update(payload).eq("id", initial.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("materials").insert({ ...payload, user_id: user.user.id, company_id: companyId, module_key: moduleKey });
        if (error) throw error;

      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(isEdit ? "Vare oppdatert" : "Vare opprettet");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-t-2xl bg-card p-5 sm:rounded-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? "Rediger vare" : "Ny vare"}</h2>
          <button onClick={onClose} className="btn btn-ghost p-2"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-base">Varenr.</label>
              <input className="input-base" value={form.item_number ?? ""} onChange={(e) => set("item_number", e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="label-base">Kategori</label>
              <select className="input-base" value={form.category_id ?? ""} onChange={(e) => set("category_id", e.target.value || null)}>
                <option value="">— Velg —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-base">Beskrivelse *</label>
            <input className="input-base" value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} required autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-base">Leverandør</label>
              <select className="input-base" value={form.supplier_id ?? ""} onChange={(e) => set("supplier_id", e.target.value || null)}>
                <option value="">— Ingen —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-base">Produsent</label>
              <input className="input-base" value={form.manufacturer ?? ""} onChange={(e) => set("manufacturer", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="label-base">Enhet</label>
              <input className="input-base" value={form.unit ?? "stk"} onChange={(e) => set("unit", e.target.value)} />
            </div>
            <div>
              <label className="label-base">Innkjøp</label>
              <input type="number" step="0.01" className="input-base" value={form.purchase_price ?? 0} onChange={(e) => set("purchase_price", Number(e.target.value))} />
            </div>
            <div>
              <label className="label-base" title="Veiledende utsalgspris fra leverandør (referanse – brukes ikke i kalkyle)">Ordinær</label>
              <input
                type="number" step="0.01"
                className="input-base"
                value={form.list_price ?? ""}
                onChange={(e) => set("list_price", e.target.value === "" ? null : Number(e.target.value))}
              />
            </div>
            <div>
              <label className="label-base" title="La stå tom for å bruke kategori/globalt påslag">Påslag %</label>
              <input
                type="number" step="0.1"
                placeholder={`(${effectiveMarkup.toFixed(0)})`}
                className="input-base"
                value={form.markup_percent ?? ""}
                onChange={(e) => set("markup_percent", e.target.value === "" ? null : Number(e.target.value))}
              />
            </div>
          </div>
          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            Effektivt påslag: <b>{effectiveMarkup.toFixed(1)}%</b>
            {!usesOverride && " (fra kategori/globalt)"} · Salgspris beregnes til <b>{formatCurrency(dynamicSale)}</b>
          </div>

          {/* Kundepris – vi setter prisen selv, systemet viser hva vi tjener. */}
          <div className="rounded-md border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <label className="label-base mb-0">Kundepris (eks. mva)</label>
              {form.customer_price_source && (
                <span className="chip">
                  {form.customer_price_source === "manual" ? "Manuell"
                    : form.customer_price_source === "markup" ? "Innkjøp + påslag"
                    : "Veiledende − rabatt"}
                </span>
              )}
            </div>
            <input
              type="number" step="0.01" inputMode="decimal"
              className="input-base"
              placeholder="Ikke satt"
              value={form.customer_price ?? ""}
              onChange={(e) =>
                setCustomerPrice(e.target.value === "" ? null : Number(e.target.value), "manual")
              }
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="btn btn-ghost text-xs"
                  disabled={!form.list_price}
                  onClick={() =>
                    setCustomerPrice(
                      Number((Number(form.list_price ?? 0) * (1 - listDiscount / 100)).toFixed(2)),
                      "list_discount",
                    )
                  }
                >
                  Veiledende −
                </button>
                <input
                  type="number" step="0.1" min="0" max="100" inputMode="decimal"
                  aria-label="Rabatt fra veiledende (%)"
                  className="input-base h-8 w-16 px-2 py-1 text-xs"
                  value={listDiscountInput}
                  onChange={(e) => {
                    setListDiscountInput(e.target.value);
                    const n = Number(e.target.value);
                    if (e.target.value !== "" && !Number.isNaN(n)) {
                      try { localStorage.setItem(LIST_DISCOUNT_KEY, String(n)); } catch { /* ignore */ }
                    }
                  }}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>

              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => setCustomerPrice(Number(dynamicSale.toFixed(2)), "markup")}
              >
                Innkjøp + {effectiveMarkup.toFixed(0)} %
              </button>
              <button
                type="button"
                className="btn btn-ghost text-xs"
                disabled={form.customer_price == null}
                onClick={() =>
                  setCustomerPrice(
                    Math.ceil(Number(form.customer_price ?? 0) / 10) * 10,
                    form.customer_price_source ?? "manual",
                  )
                }
              >
                Rund opp til 10
              </button>
              {form.customer_price != null && (
                <button type="button" className="btn btn-ghost text-xs" onClick={() => setCustomerPrice(null, null)}>
                  Nullstill
                </button>
              )}
            </div>
            <div className="mt-2 text-xs">
              Innkjøp <b>{formatCurrency(cost)}</b>
              {liveMargin ? (
                <span className={`ml-2 ${marginTone(liveMargin.dg)}`}>
                  DB <b>{formatCurrency(liveMargin.db)}</b> · DG <b>{liveMargin.dg.toFixed(1).replace(".", ",")} %</b>
                </span>
              ) : (
                <span className="ml-2 text-muted-foreground">— sett kundepris for å se DB/DG</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={Boolean(form.is_small_part)} onChange={(e) => set("is_small_part", e.target.checked)} />
              Smådel — inngår i kalkylen, men skjules på tilbud (kan samles til «Installasjonsmateriell»)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={Boolean(form.is_favorite)} onChange={(e) => set("is_favorite", e.target.checked)} />
              Favoritt — vises først i hurtigsøk
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Avbryt</button>
            <button type="submit" disabled={save.isPending} className="btn btn-primary">{isEdit ? "Lagre" : "Opprett"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
